import { Component, h, State, Prop, Event, EventEmitter } from '@stencil/core';
import { Size, Kind, DeprecatedKind } from '../aha-button/types';

export interface AhaCopyButton extends HTMLAhaCopyButtonElement {}

@Component({
  tag: 'aha-copy-button',
  shadow: true,
})
export class AhaCopyButton {
  @State() isCopied: boolean = false;
  @State() timeoutId: number = 0;

  /**
   * The text to copy to clipboard when button is clicked (optional)
   */
  @Prop() text?: string;
  /**
   * A callback function to get the text to copy (optional)
   * If provided, this will be used instead of the text prop
   */
  getText?: () => string;

  /** The appearance of the button. The "default" kind is the default style. */
  @Prop({ reflect: true }) kind: Kind | DeprecatedKind = 'icon';

  /** The size of the button. */
  @Prop({ reflect: true }) size: Size = 'small';

  /**
   * Copies the text to clipboard and shows success icon
   */
  private copyToClipboard = async () => {
    if (this.isCopied) return;

    try {
      // Get text either from the prop or via the callback
      let textToCopy = this.text;

      if (!textToCopy) {
        if (this.getText) {
          // Try to get text from the callback
          textToCopy = this.getText();
        }

        if (!textToCopy) {
          console.warn('No text provided for copy operation');
          return;
        }
      }

      await navigator.clipboard.writeText(textToCopy);
      this.isCopied = true;

      // Clear any existing timeout
      if (this.timeoutId) {
        window.clearTimeout(this.timeoutId);
      }

      // Set timeout to revert icon after 5 seconds
      this.timeoutId = window.setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  render() {
    return (
      <aha-button
        kind={this.kind}
        size={this.size}
        onClick={this.copyToClipboard}
        aria-label={this.isCopied ? 'Copied' : 'Copy to clipboard'}
      >
        {this.isCopied ? (
          <aha-icon icon="far fa-check"></aha-icon>
        ) : (
          <aha-icon icon="far fa-copy"></aha-icon>
        )}
      </aha-button>
    );
  }
}
